<template>
  <section class="mx-auto max-w-7xl px-6 py-10">
    <div class="rounded-[2rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(217,119,6,0.24),_transparent_30%),rgba(9,14,26,0.92)] p-6 shadow-glow">
      <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="max-w-3xl">
          <p class="text-xs uppercase tracking-[0.35em] text-amber-200/80">{{ $t('tournamentPage.eyebrow') }}</p>
          <h1 class="mt-3 font-display text-3xl font-semibold text-white sm:text-5xl">{{ $t('tournamentPage.title') }}</h1>
          <p class="mt-4 text-sm leading-6 text-steel-200">
            {{ $t('tournamentPage.description') }}
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-3">
          <div class="rounded-3xl border border-white/10 bg-white/5 px-5 py-4">
            <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('tournamentPage.openRooms') }}</p>
            <p class="mt-2 text-2xl font-semibold text-white">{{ openRoomCount }}</p>
          </div>
          <div class="rounded-3xl border border-white/10 bg-white/5 px-5 py-4">
            <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('tournamentPage.entrants') }}</p>
            <p class="mt-2 text-2xl font-semibold text-white">{{ entrantCount }}/16</p>
          </div>
          <div class="rounded-3xl border border-white/10 bg-white/5 px-5 py-4">
            <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('tournamentPage.champion') }}</p>
            <p class="mt-2 text-lg font-semibold text-white">{{ championName }}</p>
          </div>
        </div>
      </div>

      <div class="mt-6 flex flex-wrap items-center gap-3">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">
          {{ currentUserEntry ? `You are in ${currentUserEntry.roundHeading} / ${currentUserEntry.roomName}` : $t('tournamentPage.chooseOpeningRoom') }}
        </p>
        <button
          v-if="isAdmin"
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="resetting"
          @click="resetTournament"
        >
          {{ resetting ? $t('tournamentPage.resetting') : $t('tournamentPage.resetTournament') }}
        </button>
      </div>

      <p v-if="statusMessage" class="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{{ statusMessage }}</p>
      <p v-if="errorMessage" class="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{{ errorMessage }}</p>
    </div>

    <div class="mt-10 grid gap-8 xl:grid-cols-[1.2fr,0.8fr]">
      <div class="space-y-8">
        <section v-for="round in roundsWithLabels" :key="round.key">
          <div class="mb-4 flex items-center justify-between">
            <div>
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ round.label }}</p>
              <h2 class="mt-1 font-display text-2xl font-semibold text-white">{{ round.heading }}</h2>
            </div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-500">{{ round.matches.length }} {{ $t('tournamentPage.roomsCount') }}</p>
          </div>

          <div class="grid gap-4 lg:grid-cols-2">
            <article
              v-for="match in round.matches"
              :key="match.id"
              class="cursor-pointer rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-5 transition hover:border-white/20 hover:bg-white/[0.06]"
              @click="goToRoom(match.id)"
            >
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ match.roomName }}</p>
                  <p class="mt-1 text-sm text-steel-300">{{ matchStatus(match) }}</p>
                </div>
                <span class="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em]" :class="match.winnerId ? 'bg-emerald-500/20 text-emerald-100' : 'bg-white/10 text-steel-300'">
                  {{ match.winnerId ? $t('tournamentPage.advanced') : $t('tournamentPage.open') }}
                </span>
              </div>

              <div class="mt-4 grid gap-4">
                <div
                  v-for="(entrant, entrantIndex) in match.entrants"
                  :key="entrant?.id || `${match.id}-${entrantIndex}`"
                  class="overflow-hidden rounded-3xl border"
                  :class="entrantCardClass(match, entrant)"
                >
                  <template v-if="entrant">
                    <div class="space-y-3 p-4">
                      <div class="flex items-center gap-3">
                        <div class="h-11 w-11 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
                          <img v-if="entrant.avatar" :src="entrant.avatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
                          <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ entrant.initials }}</div>
                        </div>
                        <div class="min-w-0">
                          <p class="truncate text-sm font-semibold text-white">{{ entrant.displayName }}</p>
                          <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ entrant.handle }}</p>
                        </div>
                      </div>
                      <div class="flex items-center justify-between gap-3">
                        <p class="text-sm font-semibold text-white">{{ entrant.carName }}</p>
                        <span class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ entrant.votes }} votes</span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="p-5">
                      <p class="text-xs uppercase tracking-[0.3em] text-steel-500">{{ $t('tournamentPage.slot') }} {{ entrantIndex + 1 }}</p>
                      <p class="mt-2 text-sm text-steel-300">{{ joinPlaceholder(match) }}</p>
                      <button v-if="canJoinMatch(match)" type="button" class="mt-4 rounded-2xl bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-amber-300" @click.stop="openJoinModal(match.id)">
                        {{ $t('tournamentPage.joinRoom') }}
                      </button>
                    </div>
                  </template>
                </div>
              </div>
              <div class="mt-4 rounded-2xl border border-white/5 bg-midnight-900/50 px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('tournamentPage.tapRoom') }}
              </div>
            </article>
          </div>
        </section>
      </div>

      <aside class="space-y-6">
        <div class="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-6">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('tournamentPage.howItWorks') }}</p>
          <div class="mt-4 space-y-3 text-sm text-steel-300">
            <p>{{ $t('tournamentPage.step1') }}</p>
            <p>{{ $t('tournamentPage.step2') }}</p>
            <p>{{ $t('tournamentPage.step3', { count: voteTarget }) }}</p>
            <p>{{ $t('tournamentPage.step4') }}</p>
          </div>
        </div>

        <div class="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-6">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('tournamentPage.bracketPulse') }}</p>
          <div class="mt-4 space-y-4">
            <div v-for="round in roundsWithLabels" :key="`${round.key}-summary`" class="rounded-3xl border border-white/10 bg-midnight-900/50 p-4">
              <div class="flex items-center justify-between">
                <p class="text-sm font-semibold text-white">{{ round.heading }}</p>
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ round.matches.filter((match) => match.winnerId).length }}/{{ round.matches.length }} {{ $t('tournamentPage.complete') }}</p>
              </div>
              <div class="mt-3 grid gap-2">
                <div v-for="match in round.matches" :key="`${match.id}-mini`" class="flex items-center justify-between rounded-2xl border border-white/5 bg-white/[0.03] px-3 py-2 text-xs text-steel-300">
                  <span>{{ match.roomName }}</span>
                  <span class="font-semibold text-white">{{ winnerLabel(match) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <div v-if="showJoinModal" class="fixed inset-0 z-[140] overflow-y-auto bg-black/70 px-4 py-4 backdrop-blur-sm sm:px-6 sm:py-6" @click.self="closeJoinModal">
      <div class="mx-auto flex min-h-full w-full max-w-xl items-center justify-center">
        <div class="flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-midnight-900/95 shadow-glow sm:max-h-[calc(100vh-3rem)]">
          <div class="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-5 sm:px-6">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('tournamentPage.joinTournament') }}</p>
            <h3 class="mt-1 font-display text-2xl font-semibold text-white">{{ selectedRoomLabel }}</h3>
          </div>
          <button type="button" class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white" @click="closeJoinModal">{{ $t('tournamentPage.close') }}</button>
          </div>

          <div class="flex-1 overflow-y-auto px-5 py-5 pr-3 sm:px-6 sm:pr-4">
            <div class="grid gap-4">
              <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
                {{ $t('tournamentPage.carName') }}
                <input v-model.trim="joinForm.carName" type="text" placeholder="BMW M4 G82" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-amber-400/70 focus:outline-none focus:ring-2 focus:ring-amber-400/30" />
              </label>
              <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
                {{ $t('tournamentPage.buildSpecs') }}
                <textarea v-model.trim="joinForm.specs" rows="4" placeholder="3.0L twin turbo, 710hp, xDrive..." class="mt-2 w-full resize-none rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-amber-400/70 focus:outline-none focus:ring-2 focus:ring-amber-400/30"></textarea>
              </label>
              <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10">
                <input class="absolute left-0 top-0 h-0 w-0 opacity-0" type="file" accept="image/*" @change="handleJoinPhoto" />
                {{ $t('tournamentPage.uploadCarPhoto') }}
              </label>
              <div
                v-if="joinForm.photoPreview"
                class="relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 bg-black"
                style="touch-action: none;"
                @pointerdown="startJoinPreviewDrag($event)"
              >
                <img
                  :src="joinForm.photoPreview"
                  alt="Tournament build preview"
                  class="h-full w-full select-none object-cover"
                  :style="{ objectPosition: joinPreviewPosition }"
                  draggable="false"
                />
                <div class="pointer-events-none absolute inset-x-0 bottom-3 text-center text-[10px] uppercase tracking-[0.3em] text-white/70">
                  {{ $t('tournamentPage.dragToPosition') }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-5 py-4 sm:px-6">
            <p class="text-xs text-steel-400">{{ $t('tournamentPage.imageStorageNote') }}</p>
            <button type="button" class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-70" :disabled="joinSubmitting || !canSubmitJoin" @click="submitJoin">
              {{ joinSubmitting ? $t('tournamentPage.joining') : $t('tournamentPage.enterRoom') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession } from '@/lib/supabase'

const ACTIVE_BRACKET_KEY = 'main'
const VOTE_TARGET = 5
const ROUND_CONFIG = [
  { key: 'round-1', label: 'Round 1', heading: 'Opening Rooms', roundNumber: 1 },
  { key: 'round-2', label: 'Round 2', heading: 'Quarter Finals', roundNumber: 2 },
  { key: 'round-3', label: 'Round 3', heading: 'Semi Finals', roundNumber: 3 },
  { key: 'round-4', label: 'Round 4', heading: 'Final Room', roundNumber: 4 },
]

export default {
  name: 'TournamentRoomsView',
  data() {
    return {
      voteTarget: VOTE_TARGET,
      rooms: [],
      entries: [],
      votes: [],
      sessionUser: null,
      currentProfile: null,
      resetting: false,
      showJoinModal: false,
      selectedRoomId: '',
      joinSubmitting: false,
      joinForm: { carName: '', specs: '', photoFile: null, photoPreview: '' },
      joinPhotoOffset: { x: 50, y: 50 },
      joinPreviewDrag: null,
      errorMessage: '',
      statusMessage: '',
      liveChannel: null,
      authSubscription: null,
      visibilityHandler: null,
      refreshIntervalId: null,
    }
  },
  computed: {
    isAdmin() {
      return this.currentProfile?.role === 'admin'
    },
    selectedRoom() {
      return this.rooms.find((room) => room.id === this.selectedRoomId) || null
    },
    selectedRoomLabel() {
      return this.selectedRoom?.room_name || this.$t('tournamentPage.selectedRoomFallback')
    },
    entrantCount() {
      return this.entries.filter((entry) => entry.roundNumber === 1).length
    },
    openRoomCount() {
      return this.roundsWithLabels[0]?.matches.filter((match) => match.entrants.filter(Boolean).length < 2).length || 0
    },
    champion() {
      const finalMatch = this.roundsWithLabels[this.roundsWithLabels.length - 1]?.matches?.[0]
      return finalMatch?.entrants.find((entrant) => entrant?.id === finalMatch?.winnerId) || null
    },
    championName() {
      return this.champion ? this.champion.carName : 'Not decided'
    },
    currentUserEntry() {
      const userId = this.sessionUser?.id
      if (!userId) return null
      const allEntries = []
      this.roundsWithLabels.forEach((round) => {
        round.matches.forEach((match) => {
          match.entrants.forEach((entrant) => {
            if (entrant?.userId === userId) {
              allEntries.push({ ...entrant, roundHeading: round.heading, roundNumber: round.roundNumber, roomName: match.roomName })
            }
          })
        })
      })
      return allEntries.sort((a, b) => b.roundNumber - a.roundNumber)[0] || null
    },
    canSubmitJoin() {
      return Boolean(this.sessionUser?.id && this.selectedRoomId && this.joinForm.carName && this.joinForm.specs && this.joinForm.photoFile)
    },
    joinPreviewPosition() {
      return `${this.joinPhotoOffset.x}% ${this.joinPhotoOffset.y}%`
    },
    roundsWithLabels() {
      const entriesByRoom = new Map()
      const voteCounts = new Map()
      const userVotesByRoom = new Map()

      this.votes.forEach((vote) => {
        const voteKey = `${vote.room_id}:${vote.entry_id}`
        voteCounts.set(voteKey, (voteCounts.get(voteKey) || 0) + 1)
        userVotesByRoom.set(`${vote.room_id}:${vote.voter_id}`, vote.entry_id)
      })

      this.entries.forEach((entry) => {
        if (!entriesByRoom.has(entry.room_id)) entriesByRoom.set(entry.room_id, [])
        entriesByRoom.get(entry.room_id).push({
          id: entry.id,
          userId: entry.user_id,
          slotNumber: entry.slot_number,
          carName: entry.car_name,
          specs: entry.specs,
          photo: entry.photo,
          photoPosition: `${entry.photoOffsetX}% ${entry.photoOffsetY}%`,
          displayName: entry.displayName,
          handle: entry.handle,
          avatar: entry.avatar,
          initials: entry.initials,
          votes: voteCounts.get(`${entry.room_id}:${entry.id}`) || 0,
        })
      })

      return ROUND_CONFIG.map((round) => ({
        ...round,
        matches: this.rooms
          .filter((room) => room.round_number === round.roundNumber)
          .sort((a, b) => a.match_number - b.match_number)
          .map((room) => {
            const slots = [null, null]
            ;(entriesByRoom.get(room.id) || []).forEach((entrant) => {
              const index = Math.max(0, Math.min(1, (entrant.slotNumber || 1) - 1))
              slots[index] = entrant
            })
            return {
              id: room.id,
              roomName: room.room_name,
              roundNumber: room.round_number,
              winnerId: room.winner_entry_id,
              entrants: slots,
              currentUserVote: userVotesByRoom.get(`${room.id}:${this.sessionUser?.id || ''}`) || '',
            }
          }),
      }))
    },
  },
  async mounted() {
    this.sessionUser = (await refreshReadySession())?.user || null
    await this.fetchCurrentProfile()
    await this.ensureBracket()
    await this.fetchTournament()
    this.setupRealtime()

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      this.sessionUser = session?.user || null
      await this.fetchCurrentProfile()
      await this.fetchTournament()
    })
    this.authSubscription = authListener?.subscription || null

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.recoverLiveData()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
    this.refreshIntervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.fetchTournament()
      }
    }, 15000)
  },
  beforeUnmount() {
    if (this.liveChannel) supabase.removeChannel(this.liveChannel)
    if (this.authSubscription) this.authSubscription.unsubscribe()
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    if (this.refreshIntervalId) {
      window.clearInterval(this.refreshIntervalId)
      this.refreshIntervalId = null
    }
  },
  methods: {
    async recoverLiveData() {
      this.sessionUser = (await refreshReadySession())?.user || null
      await this.fetchCurrentProfile()
      await this.fetchTournament()
      this.setupRealtime()
    },
    async ensureBracket() {
      const { error } = await supabase.rpc('ensure_tournament_bracket', { p_bracket_key: ACTIVE_BRACKET_KEY })
      if (error) {
        this.errorMessage = 'Tournament tables are not ready in Supabase yet. Run supabase/tournament_tables.sql first.'
      }
    },
    async fetchCurrentProfile() {
      if (!this.sessionUser?.id) {
        this.currentProfile = null
        return
      }
      const { data } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path, role')
        .eq('id', this.sessionUser.id)
        .maybeSingle()
      this.currentProfile = data || null
    },
    async fetchTournament() {
      this.errorMessage = ''
      const { data: rooms, error: roomError } = await supabase
        .from('tournament_rooms')
        .select('id, bracket_key, round_number, match_number, room_name, winner_entry_id, status')
        .eq('bracket_key', ACTIVE_BRACKET_KEY)
        .order('round_number', { ascending: true })
        .order('match_number', { ascending: true })

      if (roomError) {
        this.errorMessage = roomError.message || 'Unable to load tournament rooms.'
        return
      }

      this.rooms = rooms || []
      const roomIds = this.rooms.map((room) => room.id)
      if (!roomIds.length) {
        this.entries = []
        this.votes = []
        return
      }

      const [{ data: entries, error: entryError }, { data: votes, error: voteError }] = await Promise.all([
        supabase
          .from('tournament_entries')
          .select('id, room_id, user_id, slot_number, car_name, specs, photo_path, photo_url, photo_offset_x, photo_offset_y')
          .eq('bracket_key', ACTIVE_BRACKET_KEY)
          .in('room_id', roomIds)
          .order('slot_number', { ascending: true }),
        supabase
          .from('tournament_votes')
          .select('id, room_id, entry_id, voter_id')
          .eq('bracket_key', ACTIVE_BRACKET_KEY)
          .in('room_id', roomIds),
      ])

      if (entryError || voteError) {
        this.errorMessage = entryError?.message || voteError?.message || 'Unable to load tournament data.'
        return
      }

      const userIds = [...new Set((entries || []).map((entry) => entry.user_id).filter(Boolean))]
      let profilesById = new Map()
      if (userIds.length) {
        const { data: profiles } = await supabase.from('profiles').select('id, handle, full_name, avatar_url, avatar_path').in('id', userIds)
        profilesById = new Map((profiles || []).map((profile) => [profile.id, profile]))
      }

      this.entries = (entries || []).map((entry) => {
        const profile = profilesById.get(entry.user_id) || {}
        const fullName = profile.full_name || 'DRVN Driver'
        return {
          ...entry,
          roundNumber: this.rooms.find((room) => room.id === entry.room_id)?.round_number || 1,
          displayName: fullName,
          handle: profile.handle || 'member',
          avatar: getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url }),
          initials: fullName.split(' ').filter(Boolean).slice(0, 2).map((chunk) => chunk[0].toUpperCase()).join('') || 'D',
          photoOffsetX: Number.isFinite(entry.photo_offset_x) ? entry.photo_offset_x : 50,
          photoOffsetY: Number.isFinite(entry.photo_offset_y) ? entry.photo_offset_y : 50,
          photo: getPostImageUrl({ imagePath: entry.photo_path, imageUrl: entry.photo_url }),
        }
      })
      this.votes = votes || []
    },
    setupRealtime() {
      if (this.liveChannel) supabase.removeChannel(this.liveChannel)
      this.liveChannel = supabase
        .channel('tournament-live')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_rooms' }, () => this.fetchTournament())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_entries' }, () => this.fetchTournament())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_votes' }, () => this.fetchTournament())
        .subscribe()
    },
    matchStatus(match) {
      if (match.winnerId) return `${this.winnerLabel(match)} advanced to the next room.`
      if (match.entrants.every(Boolean)) return `Voting live. First to ${this.voteTarget} votes advances.`
      return match.roundNumber === 1 ? 'Waiting for two drivers to lock in.' : 'Waiting for winners from the previous rooms.'
    },
    joinPlaceholder(match) {
      if (match.roundNumber !== 1) return 'This slot is reserved for a winner from the previous round.'
      if (this.currentUserEntry) return 'You already joined the bracket. Keep pushing in your current room.'
      return 'Open slot for a new driver and build card.'
    },
    entrantCardClass(match, entrant) {
      if (!entrant) return 'border-dashed border-white/10 bg-white/[0.02]'
      if (match.winnerId === entrant.id) return 'border-emerald-400/40 bg-emerald-500/10'
      return 'border-white/10 bg-midnight-900/60'
    },
    winnerLabel(match) {
      const winner = match.entrants.find((entrant) => entrant?.id === match.winnerId)
      return winner ? winner.carName : 'Pending'
    },
    goToRoom(roomId) {
      this.$router.push(`/tournament/room/${roomId}`)
    },
    canJoinMatch(match) {
      return match.roundNumber === 1 && match.entrants.filter(Boolean).length < 2 && !this.currentUserEntry
    },
    openJoinModal(roomId) {
      if (!this.sessionUser?.id) {
        this.errorMessage = 'Please log in before joining a room.'
        return
      }
      if (this.currentUserEntry) {
        this.errorMessage = 'You already joined the tournament.'
        return
      }
      this.errorMessage = ''
      this.statusMessage = ''
      this.selectedRoomId = roomId
      this.joinForm = { carName: '', specs: '', photoFile: null, photoPreview: '' }
      this.joinPhotoOffset = { x: 50, y: 50 }
      this.showJoinModal = true
    },
    closeJoinModal() {
      this.showJoinModal = false
      this.selectedRoomId = ''
      this.joinSubmitting = false
      this.joinForm = { carName: '', specs: '', photoFile: null, photoPreview: '' }
      this.joinPhotoOffset = { x: 50, y: 50 }
      this.joinPreviewDrag = null
    },
    handleJoinPhoto(event) {
      const [file] = Array.from(event.target.files || [])
      if (!file) return
      this.joinForm.photoFile = file
      this.joinPhotoOffset = { x: 50, y: 50 }
      const reader = new FileReader()
      reader.onload = () => {
        this.joinForm.photoPreview = typeof reader.result === 'string' ? reader.result : ''
      }
      reader.readAsDataURL(file)
      if (event?.target) event.target.value = ''
    },
    startJoinPreviewDrag(event) {
      event.preventDefault()
      const target = event.currentTarget
      if (!target) return
      if (typeof target.setPointerCapture === 'function') {
        try {
          target.setPointerCapture(event.pointerId)
        } catch (e) {
          // ignore if capture fails
        }
      }
      const rect = target.getBoundingClientRect()
      this.joinPreviewDrag = {
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: this.joinPhotoOffset.x,
        startOffsetY: this.joinPhotoOffset.y,
        width: rect.width || 1,
        height: rect.height || 1,
      }
      const onMove = (moveEvent) => {
        if (!this.joinPreviewDrag) return
        moveEvent.preventDefault()
        const dx = moveEvent.clientX - this.joinPreviewDrag.startX
        const dy = moveEvent.clientY - this.joinPreviewDrag.startY
        const nextX = this.joinPreviewDrag.startOffsetX + (dx / this.joinPreviewDrag.width) * 100
        const nextY = this.joinPreviewDrag.startOffsetY + (dy / this.joinPreviewDrag.height) * 100
        this.joinPhotoOffset = {
          x: Math.max(0, Math.min(100, Math.round(nextX))),
          y: Math.max(0, Math.min(100, Math.round(nextY))),
        }
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        this.joinPreviewDrag = null
      }
      window.addEventListener('pointermove', onMove, { passive: false })
      window.addEventListener('pointerup', onUp)
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.82 }) {
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image decode failed.'))
        img.src = URL.createObjectURL(file)
      })
      const scale = Math.min(1, maxWidth / (image.width || 1))
      const targetWidth = Math.max(1, Math.round((image.width || 1) * scale))
      const targetHeight = Math.max(1, Math.round((image.height || 1) * scale))
      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight)
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
      if (!blob) throw new Error('Image conversion failed.')
      return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
    },
    async uploadJoinPhoto() {
      const file = this.joinForm.photoFile
      if (!file || !this.sessionUser?.id) throw new Error('Missing join photo.')
      const uploadFile = await this.convertImageToWebp(file, { maxWidth: 1600 })
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.[^.]+$/, '.webp')
      const filePath = `tournament/${this.sessionUser.id}/${Date.now()}-${safeName}`
      const { error: uploadError } = await supabase.storage.from('posts').upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })
      if (uploadError) throw new Error('Photo upload failed. Make sure the "posts" bucket exists.')
      return { filePath, publicUrl: getPostImageUrl({ imagePath: filePath }) }
    },
    async submitJoin() {
      if (!this.canSubmitJoin || !this.selectedRoomId) return
      this.joinSubmitting = true
      this.errorMessage = ''
      this.statusMessage = ''
      try {
        const { filePath, publicUrl } = await this.uploadJoinPhoto()
        const { error } = await supabase.rpc('join_tournament_room', {
          p_bracket_key: ACTIVE_BRACKET_KEY,
          p_room_id: this.selectedRoomId,
          p_car_name: this.joinForm.carName,
          p_specs: this.joinForm.specs,
          p_photo_path: filePath,
          p_photo_url: publicUrl,
          p_photo_offset_x: this.joinPhotoOffset.x,
          p_photo_offset_y: this.joinPhotoOffset.y,
        })
        if (error) throw error
        this.statusMessage = `${this.joinForm.carName} entered ${this.selectedRoomLabel}.`
        this.closeJoinModal()
        await this.fetchTournament()
      } catch (error) {
        this.errorMessage = error.message || 'Unable to join the room.'
      } finally {
        this.joinSubmitting = false
      }
    },
    async castVote(roomId, entryId) {
      this.errorMessage = ''
      this.statusMessage = ''
      if (!this.sessionUser?.id) {
        this.errorMessage = 'Please log in before voting.'
        return
      }
      const { data, error } = await supabase.rpc('cast_tournament_vote', {
        p_bracket_key: ACTIVE_BRACKET_KEY,
        p_room_id: roomId,
        p_entry_id: entryId,
      })
      if (error) {
        this.errorMessage = error.message || 'Unable to cast vote.'
        return
      }
      this.statusMessage = data?.[0]?.winner_entry_id ? 'Vote counted and the winner was advanced automatically.' : 'Vote counted.'
      await this.fetchTournament()
    },
    async resetTournament() {
      if (!this.isAdmin) return
      if (!window.confirm('Reset the full shared tournament bracket for every user?')) return
      this.resetting = true
      this.errorMessage = ''
      this.statusMessage = ''
      const { error } = await supabase.rpc('reset_tournament_bracket', { p_bracket_key: ACTIVE_BRACKET_KEY })
      if (error) {
        this.errorMessage = error.message || 'Unable to reset the tournament.'
        this.resetting = false
        return
      }
      this.statusMessage = 'Tournament bracket reset for all users.'
      this.resetting = false
      await this.fetchTournament()
    },
  },
}
</script>
